import { IBookInterval } from "@/domain/entities/lineInfo";
import { ParseError } from "@/repositories/shared/errors/parseError";
import { injectable } from "inversify";
import { err, ok, Result } from "neverthrow";
import {
  HttpBookDateCode,
  HttpBookInterval,
  HttpBookTimeCode,
  IBookIntervalParser,
} from "../lineInfoRepository.types";
import { BookInterval } from "@/domain/entities/lineInfo/bookInterval";
import {
  BookDateCode,
  BookTimeCode,
  WeekdayCode,
  WeekdayCodeName,
} from "@/types";

function parseBookDateCode(bookDateCode: HttpBookDateCode) {
  switch (bookDateCode) {
    case HttpBookDateCode.FIXED:
      return BookDateCode.FIXED;

    case HttpBookDateCode.DAYAFT:
      return BookDateCode.DAYAFT;

    case HttpBookDateCode.DAY:
      return BookDateCode.DAY;

    case HttpBookDateCode.FIRSTDAY:
      return BookDateCode.FIRSTDAY;

    case HttpBookDateCode.BOARD:
      return BookDateCode.BOARD;

    case HttpBookDateCode.BEGIN:
      return BookDateCode.BEGIN;

    case HttpBookDateCode.END:
      return BookDateCode.END;

    case HttpBookDateCode.WEEK:
      return BookDateCode.WEEK;

    case HttpBookDateCode.WINDOW:
      return BookDateCode.WINDOW;

    case HttpBookDateCode.NONE:
      return BookDateCode.NONE;
  }
}

function parseBookTimeCode(bookTimeCode: HttpBookTimeCode) {
  switch (bookTimeCode) {
    case HttpBookTimeCode.FIXED:
      return BookTimeCode.FIXED;

    case HttpBookTimeCode.BEGIN:
      return BookTimeCode.BEGIN;

    case HttpBookTimeCode.END:
      return BookTimeCode.END;

    case HttpBookTimeCode.BOARD:
      return BookTimeCode.BOARD;

    case HttpBookTimeCode.NONE:
      return BookTimeCode.NONE;
  }
}

function convertWeekdayName(weekdayCode: string): WeekdayCodeName {
  const weekdayCodeMap: Record<string, WeekdayCodeName> = {
    [WeekdayCode.MON]: WeekdayCodeName.MON,
    [WeekdayCode.TUE]: WeekdayCodeName.TUE,
    [WeekdayCode.WED]: WeekdayCodeName.WED,
    [WeekdayCode.THU]: WeekdayCodeName.THU,
    [WeekdayCode.FRI]: WeekdayCodeName.FRI,
    [WeekdayCode.SAT]: WeekdayCodeName.SAT,
    [WeekdayCode.SUN]: WeekdayCodeName.SUN,
    [WeekdayCode.HOLI]: WeekdayCodeName.HOLI,
  };

  return weekdayCodeMap[weekdayCode] ?? WeekdayCodeName.NONE;
}

export function parseBookInterval(data: HttpBookInterval): IBookInterval {
  try {
    const bookIntervalData: IBookInterval = {
      bookIntervalCode: data.bookIntervalCode,
      bookIntervalName: data.bookIntervalName,
      seatsSelectable: data.memberSeatAssignYn,
      lineTurnUid: data.lineTurnUid,
      lineRouteNo: data.lineRouteNo,
      lineRouteName: data.lineRouteName,
      lineTypeCode: data.lineTypeCode,
      bookPointCode: data.bookPointCode,
      bookBeginDateCode: parseBookDateCode(data.bookBeginDateCode),
      bookBeginYear: data.bookBeginYear,
      bookBeginMonth: data.bookBeginMonth,
      bookBeginWeek: data.bookBeginWeek,
      bookBeginWeekdayCode: data.bookBeginWeekdayCode,
      bookBeginWeekdayName: convertWeekdayName(data.bookBeginWeekdayCode ?? ""),
      bookBeginDay: data.bookBeginDay,
      bookBeginTimeCode: parseBookTimeCode(data.bookBeginTimeCode),
      bookBeginHour: data.bookBeginHour,
      bookBeginMin: data.bookBeginMin,
      bookEndDateCode: parseBookDateCode(data.bookEndDateCode),
      bookEndYear: data.bookEndYear,
      bookEndMonth: data.bookEndMonth,
      bookEndDay: data.bookEndDay,
      bookEndTimeCode: parseBookTimeCode(data.bookEndTimeCode),
      bookEndHour: data.bookEndHour,
      bookEndMin: data.bookEndMin,
      bookCancelDateCode: parseBookDateCode(data.bookCancelDateCode),
      bookCancelYear: data.bookCancelYear,
      bookCancelMonth: data.bookCancelMonth,
      bookCancelDay: data.bookCancelDay,
      bookCancelTimeCode: parseBookTimeCode(data.bookCancelTimeCode),
      bookCancelHour: data.bookCancelHour,
      bookCancelMin: data.bookCancelMin,
      displayOrder: data.displayOrder,
    };
    const bookInterval = new BookInterval(bookIntervalData);
    return bookInterval;
  } catch (error: any) {
    throw new Error(`Error parsing BookInterval: ${error.message}`);
  }
}

@injectable()
export class BookIntervalParser implements IBookIntervalParser {
  constructor() {}

  toDomain(data: HttpBookInterval): Result<IBookInterval, ParseError> {
    try {
      return ok(parseBookInterval(data));
    } catch (error: any) {
      return err(new ParseError(error.message));
    }
  }
}
